import * as React from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Well, Drawer, executeCleanDrawerRender } from '@pushly/aqe/lib/components'
import { EditOutlined, WarningFilled } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { InputNumber, notification, Radio, Select, Skeleton, Switch } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { legacyValidateFields } from '@halo/_utils/antd'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { AccountService } from '@halo/services/account.service'
import CopyToClipboard from 'react-copy-to-clipboard'
import { SUPPORTED_BILLING_CURRENCIES } from '@halo/constants'
import * as randomstring from 'randomstring'
import './style/account-billing-well.scss'

interface IAccountBillingEditor extends FormComponentProps {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IABEState {}

export const AccountBillingEditor = Form.create<IAccountBillingEditor>()(
    class extends React.Component<IAccountBillingEditor, IABEState> {
        public state: IABEState = {}

        protected appService: AppService
        protected accountService: AccountService

        public constructor(props: IAccountBillingEditor) {
            super(props)

            this.appService = Container.get(AppService)
            this.accountService = Container.get(AccountService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const { account } = this.props

            const formItemLayout = {
                labelCol: {
                    xs: { span: 24 },
                    sm: { span: 6 },
                },
                wrapperCol: {
                    xs: { span: 24 },
                    sm: { span: 18 },
                },
            }

            return (
                <Drawer
                    className={classnames('adf-billing-editor')}
                    title="Update Billing"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form {...formItemLayout}>
                        <Form.Item label="Billing Type" className={classnames('adf-billing-editor-type')}>
                            {this.props.form.getFieldDecorator('type', {
                                initialValue: this.currentValues.type,
                            })(
                                <Select size="small">
                                    <Select.Option value="INVOICE">Invoicing</Select.Option>
                                    <Select.Option value="CREDIT">Credit Card</Select.Option>
                                </Select>,
                            )}
                        </Form.Item>

                        <Form.Item label="Currency" className={classnames('adf-billing-editor-currency')}>
                            {this.props.form.getFieldDecorator('currency', {
                                initialValue: this.currentValues.currency,
                            })(
                                <Select size="small" showSearch={false}>
                                    {SUPPORTED_BILLING_CURRENCIES.map((cur) => (
                                        <Select.Option key={cur.value} value={cur.value}>
                                            {cur.label}
                                        </Select.Option>
                                    ))}
                                </Select>,
                            )}
                        </Form.Item>

                        <Form.Item label="Rate Type" className={classnames('adf-billing-editor-credit-type')}>
                            {this.props.form.getFieldDecorator('rate_type', {
                                initialValue: this.currentValues.rate_type,
                            })(
                                <Select size="small">
                                    <Select.Option value="FLAT_RATE">Flat Monthly Rate</Select.Option>
                                    <Select.Option value="VARIABLE_SUBSCRIBER">Variable Subscriber Rate</Select.Option>
                                    <Select.Option value="REVSHARE-PV">Revshare - Page Views</Select.Option>
                                </Select>,
                            )}
                        </Form.Item>

                        <Form.Item label="Rate Amount" className={classnames('adf-billing-editor-rate')}>
                            {this.props.form.getFieldDecorator('rate', {
                                initialValue: this.currentValues.rate,
                                rules: [
                                    {
                                        required: true,
                                        message: 'Rate Amount is required.',
                                    },
                                ],
                            })(
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    step={1}
                                    decimalSeparator={'.'}
                                    formatter={(value) =>
                                        `${this.currentCurrencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                    }
                                />,
                            )}
                        </Form.Item>
                    </Form>
                </Drawer>
            )
        }

        protected get account(): AccountDto {
            return this.props.account
        }

        protected get propsValue(): any {
            return this.props.account.billing_data || {}
        }

        protected get formValue(): any {
            return this.props.form.getFieldsValue()
        }

        protected get currentValues() {
            const propsValue = this.propsValue
            const formValue = this.formValue

            let values: any = {
                type: (formValue.type || propsValue.type || 'INVOICE').toUpperCase(),
                currency: (formValue.currency || propsValue.currency || 'USD').toUpperCase(),
                rate_type: (formValue.rate_type || propsValue.rate_type || 'FLAT_RATE').toUpperCase(),
                rate: formValue.rate || propsValue.rate,
            }

            return values
        }

        protected get currentTypeIsCredit(): boolean {
            return this.currentValues.type === 'CREDIT'
        }

        protected get currentCurrencySymbol(): string {
            return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.symbol
        }

        protected handleSubmit = async () => {
            try {
                const values = await legacyValidateFields(this.props.form)

                const mergedValues = {
                    ...this.currentValues,
                    ...values,
                }

                let { ok, data } = await this.accountService.patchBillingData(this.account.id, mergedValues, true)

                if (ok) {
                    this.props.onAccountUpdated({
                        billing_data: data,
                    })

                    this.props.onClose(null)
                    simpleNotification('success', `Organization, ${this.account.name}, has been successfully updated.`)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IAccountBillingWell {
    account: AccountDto
    onAccountUpdated: (account: Partial<AccountDto>) => void
    loading?: boolean
    title?: string
}

interface IABWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountBillingWell extends React.Component<IAccountBillingWell, IABWState> {
    public state: IABWState = {
        showDrawer: false,
    }

    protected appService: AppService

    public constructor(props: IAccountBillingWell) {
        super(props)

        this.appService = Container.get(AppService)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Billing Configuration"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderBillingInformation()}
                            {this.renderStripeIntegration()}

                            <AccountBillingEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderBillingInformation() {
        return (
            <Well title="Billing Configuration" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Billing Type</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.currentTypeIsCredit ? 'Credit Card' : 'Invoicing'}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Currency</div>
                    <div className={classnames('form-layout-row-value')}>{this.currentCurrencyDisplayName}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Rate Type</div>
                    <div className={classnames('form-layout-row-value')}>{this.normalizeRateType()}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Rate Amount</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.currentCurrencySymbol}
                        {this.currentValues.rate}
                        {this.currentRateTypeIsPageView
                            ? ' CPM'
                            : this.currentRateTypeIsFlat
                            ? ' / month'
                            : ' / subscriber'}
                    </div>
                </div>
            </Well>
        )
    }

    protected renderStripeIntegration() {
        return (
            <Well
                title={
                    <>
                        <span>Stripe Integration </span>
                        {!!this.currentValues.stripe_customer_id && (
                            <span className="lowlight">(Client Configured)</span>
                        )}
                    </>
                }
                mode="ghost"
                hideFooter={true}
            >
                {!this.currentValues.stripe_customer_id ? (
                    <>
                        <div className="billing-no-card-config">
                            <WarningFilled />
                            <span>Awaiting client card configuration</span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Customer Reference ID</div>
                            <div className={classnames('form-layout-row-value', 'can-copy')}>
                                <CopyToClipboard
                                    onCopy={() => this.handleCopied('Customer reference ID')}
                                    text={this.currentValues.stripe_customer_id}
                                >
                                    <span>{this.currentValues.stripe_customer_id}</span>
                                </CopyToClipboard>
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Contact Email</div>
                            <div className={classnames('form-layout-row-value')}>
                                {this.currentValues.stripe_customer_email}
                            </div>
                        </div>

                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Current Card</div>
                            <div className={classnames('form-layout-row-value')}>
                                **** **** **** {this.currentValues.stripe_customer_last4}
                            </div>
                        </div>
                    </>
                )}
            </Well>
        )
    }

    protected get propsValue(): any {
        return this.props.account.billing_data || {}
    }

    protected get currentValues() {
        const propsValue = this.propsValue

        let values: any = {
            type: (propsValue.type || 'INVOICE').toUpperCase(),
            currency: (propsValue.currency || 'USD').toUpperCase(),
            rate_type: (propsValue.rate_type || 'FLAT_RATE').toUpperCase(),
            rate: propsValue.rate,
        }

        if (propsValue.stripe_customer_id) {
            values.stripe_customer_id = propsValue.stripe_customer_id
            values.stripe_customer_email = propsValue.stripe_customer_email
            values.stripe_customer_last4 = propsValue.stripe_customer_last4
        }

        return values
    }

    protected get currentTypeIsCredit(): boolean {
        return this.currentValues.type === 'CREDIT'
    }

    protected get currentRateTypeIsFlat(): boolean {
        return this.currentValues.rate_type === 'FLAT_RATE'
    }

    protected get currentRateTypeIsPageView(): boolean {
        return this.currentValues.rate_type === 'REVSHARE-PV'
    }

    protected normalizeRateType(): string {
        const rate_type = this.currentValues.rate_type
        return rate_type === 'FLAT_RATE'
            ? 'Flat Monthly Rate'
            : rate_type === 'VARIABLE_SUBSCRIBER'
            ? 'Variable Subscriber Rate'
            : 'Revshare - Page Views'
    }

    protected get currentCurrencyDisplayName(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.label
    }

    protected get currentCurrencySymbol(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.symbol
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
